import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { RemoteWidgetResponse } from 'api';
import { loadRemoteWidgetAssets } from './utils';

type AsyncRenderedRemoteProps = {
  id: string;
  widgetName: string;
  loadWidget: () => Promise<RemoteWidgetResponse>;
  fallback?: ReactNode;
  data?: Record<string, unknown>;
};

export function AsyncRenderedRemote({
  id,
  loadWidget,
  fallback,
  data,
  widgetName,
}: AsyncRenderedRemoteProps) {
  const [isWidgetReady, setIsWidgetReady] = useState(false);

  useEffect(() => {
    loadWidget().then(async (remoteWidget) => {
      await loadRemoteWidgetAssets(remoteWidget, id, widgetName);
      setIsWidgetReady(true);
    });
  }, [loadWidget, id, widgetName]);

  const Content = useMemo(() => {
    if (!isWidgetReady) return null;

    const createWidget = window.widgets?.[widgetName];

    if (typeof createWidget === 'function') {
      return createWidget(window.INITIAL_STATE?.[id]);
    }

    return null;
  }, [id, isWidgetReady, widgetName]);

  return (
    <div data-id={id} data-qa="async-rendered-remote" data-remote={widgetName} suppressHydrationWarning>
      <div data-content={id}>{Content ? <Content {...data} /> : fallback}</div>
    </div>
  );
}
